import { useEffect, useState } from 'react';

import useSWR, { type SWRConfiguration } from 'swr';

import { useClickhouseMediaAnalytics } from '@/utils/hooks/featureFlag/useClickhouseMediaAnalytics';

import { get, patch } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';

import { POST, STORY } from '@/lib/posts/postTypes';

import {
    GROUP_MEDIA as GROUP_MEDIA_ENDPOINT,
    MEDIA as MEDIA_V1_ENDPOINT,
    MEDIA_V2 as MEDIA_V2_ENDPOINT,
    STORY as STORY_ENDPOINT
} from '@/routes/apiEndpoints';

export interface UseMediaParams {
    groupID?: number;
    spID?: number;
    mediaID?: string;
    socialNetworkType?: SocialProfileNetworkType;
    type?: string;
    requestParams?: Record<string, unknown>;
    withPrevNext?: boolean;
    options?: SWRConfiguration;
}

export interface UseMediaReturn {
    media: any;
    previousMedia: any;
    nextMedia: any;
    mutateMedia: (data?: any) => Promise<any>;
    mediaError: any;
    isLoading: boolean;
}

const useMedia = ({
    groupID,
    spID,
    mediaID,
    socialNetworkType,
    type = POST,
    requestParams = {},
    withPrevNext = false,
    options = {}
}: UseMediaParams = {}): UseMediaReturn => {
    const { isActiveForNetwork } = useClickhouseMediaAnalytics();
    const isNetworkActive = socialNetworkType ? isActiveForNetwork(socialNetworkType) : false;
    const isV2 = isNetworkActive && !groupID && type !== STORY;
    const skipFollowersPatch = isNetworkActive;

    const [launchRequest, setLaunchRequest] = useState(false);

    const getEndpointUrl = (groupId?: number): string => {
        let endpoint: string;
        if (type === STORY) {
            endpoint = STORY_ENDPOINT;
        } else if (groupId) {
            endpoint = GROUP_MEDIA_ENDPOINT;
        } else if (isNetworkActive) {
            endpoint = MEDIA_V2_ENDPOINT;
        } else {
            endpoint = MEDIA_V1_ENDPOINT;
        }
        return injectEndpointUrlParams(endpoint, {
            GROUP_ID: groupId,
            SPID: spID,
            mediaID
        });
    };
    const endpointUrl = getEndpointUrl(groupID);

    const {
        data,
        mutate: mutateMedia,
        error: mediaError,
        isLoading
    } = useSWR(
        launchRequest ? endpointUrl : null,
        (url: string) =>
            get(
                url,
                !isV2 && withPrevNext ? { ...requestParams, with_pagination: true } : requestParams
            ).then((res) => res.data),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const patchMedia = async (): Promise<void> => {
        try {
            await patch(`${getEndpointUrl()}/followers`, {});
        } catch (error) {
            console.error(error);
        } finally {
            setLaunchRequest(true);
        }
    };

    useEffect(() => {
        if (skipFollowersPatch) {
            setLaunchRequest(true);
        } else if (!launchRequest) {
            patchMedia();
        }
    }, [launchRequest]);

    return {
        media: data ? (type === STORY ? data.story : data.media) : null,
        previousMedia:
            !isV2 && data ? (type === STORY ? data.previous_story : data.previous_media) : null,
        nextMedia: !isV2 && data ? (type === STORY ? data.next_story : data.next_media) : null,
        mutateMedia,
        mediaError,
        isLoading: isLoading || !launchRequest
    };
};
export default useMedia;
